import { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Globe, Monitor, Globe2, Cloud, User } from 'lucide-react';
import { C, FONTS } from '../../tokens/design';
import { useAssets } from '../../hooks/useClientData';
import { relativeTime, exactTime } from '../../utils/time';
import { directoryAssetIdentity } from '../../utils/directoryAssetIdentity';
import { ASSET_LIFECYCLE_FILTERS, assetLastSeenAt, getAssetLifecycle, summarizeAssetLifecycle } from '../../utils/assetLifecycle';
import { redactDemoText } from '../../utils/demoRedaction';
import Shell from '../../components/layout/Shell';
import Card, { CardHeader } from '../../components/ui/Card';
import EmptyState from '../../components/ui/EmptyState';
import Tag from '../../components/ui/Tag';
import PageError from '../../components/ui/PageError';
import Skeleton from '../../components/ui/Skeleton';
import { TH, TR, TD } from '../../components/ui/Table';

const ASSET_TYPE_LABELS = { 1: 'External IP', 2: 'Internal Host', 3: 'Domain', 4: 'Cloud Resource', 5: 'Account', 6: 'External Host' };
const ASSET_TYPE_COLORS = { 1: C.org, 2: C.blu, 3: C.amb, 4: C.pur, 5: C.sky, 6: C.org };
const ASSET_TYPE_ICONS = { 1: Globe, 2: Monitor, 3: Globe2, 4: Cloud, 5: User, 6: Globe };
const ASSET_TYPE_STR_MAP = {
  externalip: 1, internalhost: 2, domain: 3, cloudresource: 4, user: 5,
  external_ip: 1, internal_host: 2, cloud_resource: 4, account: 5,
};
const normalizeAssetType = (t) => {
  if (typeof t === 'number') return t;
  if (typeof t === 'string') return ASSET_TYPE_STR_MAP[t.toLowerCase()] ?? 0;
  return 0;
};

const CINCH_CLIENT_ID = '3b4ed5dd-980a-4a11-afdc-7d5ae7483109';
const CINCH_AD_ACCOUNT_NOISE = new Set([
  '1 domaindns',
  '1 serviceconnectionpoint',
  'cinchu-rds',
  'tsgateway',
]);

function isCinchAsset(asset) {
  return String(asset?.clientId || '').toLowerCase() === CINCH_CLIENT_ID;
}

export function assetPresentationType(asset) {
  const type = normalizeAssetType(asset?.assetType);
  const hostname = String(asset?.hostname || asset?.displayName || '').trim().toLowerCase();
  const hasIp = String(asset?.ipAddress || '').trim() !== '';
  if (isCinchAsset(asset) && type === 2 && !hasIp && hostname.endsWith('.cinchit.com')) return 6;
  return type;
}
const TABS = [
  { id: 'all', l: 'All', filter: null },
  { id: 'hosts', l: 'Hosts', filter: [1, 2] },
  { id: 'domains', l: 'Domains', filter: [3] },
  { id: 'cloud', l: 'Cloud', filter: [4] },
  { id: 'accounts', l: 'Accounts', filter: [5] },
];

const LIFECYCLE_COLORS = {
  active: C.grn,
  stale: C.amb,
  retired: C.t3,
  unknown: C.t2,
};

const HIDDEN_TAG_KEYS = new Set([
  'connector',
  'collector',
  'credential',
  'email',
  'fingerprint',
  'hash',
  'id',
  'implementation',
  'module',
  'principal',
  'provider',
  'source',
  'tenant',
  'token',
  'user',
  'username',
  'vendor',
]);

const FRIENDLY_TAG_LABELS = {
  cloud: 'Cloud',
  critical: 'Critical',
  email: 'Email',
  external: 'External',
  'internet-facing': 'Internet Facing',
  internal: 'Internal',
  production: 'Production',
  'pci-scope': 'PCI Scope',
};

const SENSITIVE_TAG_VALUE = /(?:@|#ext#|wireguard|secure[-_\s]?connector|credmaster|prowler|sentinel[-_\s]?command|demo-user|demo-tag|runner|scanner|tenant|subscription|secret|token|hash|fingerprint|username|vendor)/i;
const OPAQUE_TAG_VALUE = /^(?:[a-f0-9]{24,64}|[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})$/i;
const EMAIL_VALUE = /\b[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\b/i;
const IMPLEMENTATION_VALUE = /\b(?:wireguard|secure[-_\s]?connector|credmaster|prowler|sentinel[-_\s]?command|demo-user|runner|scanner|secret|token|hash)\b/i;

function titleCaseTag(value) {
  return value
    .replace(/[-_]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, c => c.toUpperCase());
}

export function parseAssetDisplayTag(tagStr) {
  if (!tagStr || typeof tagStr !== 'string') return null;
  const parts = tagStr.split(':').map(part => part.trim()).filter(Boolean);
  if (parts.length === 0) return null;

  const key = parts.length > 1 ? parts[0].toLowerCase() : '';
  const rawValue = parts[parts.length - 1];
  const value = redactDemoText(rawValue).trim();
  const normalized = value.toLowerCase();
  const fullTag = redactDemoText(parts.join(':')).toLowerCase();

  if (!value) return null;
  if (HIDDEN_TAG_KEYS.has(key)) return null;
  if (OPAQUE_TAG_VALUE.test(value) || SENSITIVE_TAG_VALUE.test(value) || SENSITIVE_TAG_VALUE.test(fullTag)) return null;
  if (/^(?:n\/a|na|none|null|unknown|undefined|tag|label)$/i.test(value)) return null;

  return FRIENDLY_TAG_LABELS[normalized] || titleCaseTag(value);
}

export function assetDisplayTags(tags) {
  if (!Array.isArray(tags)) return [];
  return [...new Set(tags.map(parseAssetDisplayTag).filter(Boolean))];
}

export function formatEmailIdentity(value) {
  if (typeof value !== 'string') return value;
  return value.replace(EMAIL_VALUE, email => email.toLowerCase());
}

function assetMetadata(asset) {
  if (!asset?.metadataJson) return {};
  if (typeof asset.metadataJson === 'object' && !Array.isArray(asset.metadataJson)) return asset.metadataJson;
  try {
    const parsed = JSON.parse(asset.metadataJson);
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
  } catch {
    return {};
  }
}

function assetEvidenceValues(asset) {
  const metadata = assetMetadata(asset);
  return [
    asset?.displayName,
    asset?.hostname,
    asset?.distinguishedName,
    asset?.principal,
    metadata.displayName,
    metadata.hostname,
    metadata.distinguishedName,
    metadata.principal,
    metadata.objectName,
    metadata.objectPath,
  ].filter(value => typeof value === 'string' && value.trim() !== '');
}

function normalizedMachineName(value) {
  if (typeof value !== 'string') return '';
  const match = value.trim().match(/^([a-z0-9][a-z0-9_.-]*)(?:\$|\s|\(|$)/i);
  return match ? match[1].replace(/\$/, '').toUpperCase() : '';
}

export function preferredAssetName(asset) {
  const displayName = typeof asset?.displayName === 'string' ? asset.displayName.trim() : '';
  const hostname = typeof asset?.hostname === 'string' ? asset.hostname.trim() : '';
  if ((!displayName || /^demo account$/i.test(displayName)) && hostname) return hostname;
  return displayName || hostname || asset?.assetId || '';
}

const AD_CONTAINER_NAMES = new Set([
  'builtin',
  'computers',
  'configuration',
  'directory service',
  'foreignsecurityprincipals',
  'optional features',
  'services',
  'sites',
  'system',
  'users',
  'windows nt',
]);

function cleanDirectoryIdentityName(value) {
  return String(value || '')
    .trim()
    .replace(/^.*\\\\/, '')
    .replace(/\$/, '')
    .replace(/[.;:]+$/, '')
    .replace(/\s+/g, ' ')
    .trim();
}

function normalizedDirectoryName(value) {
  return cleanDirectoryIdentityName(value).toLowerCase();
}

export function adDirectoryIdentity(asset) {
  const canonical = directoryAssetIdentity(asset);
  if (canonical) {
    const display = preferredAssetName(asset);
    const name = /^(?:CN|DC|OU)\s*=/i.test(display)
      ? cleanDirectoryIdentityName(canonical.dn.match(/\bCN=([^,()]+)/i)?.[1]) || asset?.assetId || ''
      : display;
    return { ...canonical, name, source: 'canonical-directory' };
  }
  if (normalizeAssetType(asset?.assetType) !== 5) return null;

  for (const value of assetEvidenceValues(asset)) {
    const machine = value.match(/\b(computer|serviceaccount)\s*:\s*([a-z0-9][a-z0-9_.-]*)\$/i);
    if (machine) {
      const name = cleanDirectoryIdentityName(machine[2]).toUpperCase();
      return name ? { kind: 'computer', name, key: `computer:${name.toLowerCase()}`, source: machine[1].toLowerCase() } : null;
    }
  }

  for (const value of assetEvidenceValues(asset)) {
    const explicit = value.match(/\b(user|group)\s*:\s*([^,(;\n]+)/i);
    if (explicit) {
      const kind = explicit[1].toLowerCase();
      const name = cleanDirectoryIdentityName(explicit[2]);
      const normalized = normalizedDirectoryName(name);
      if (normalized) return { kind, name, key: `${kind}:${normalized}`, source: kind };
    }
  }

  return null;
}

export function adComputerPrincipalName(asset) {
  const identity = adDirectoryIdentity(asset);
  return identity?.kind === 'computer' ? identity.name : '';
}

export function isAdFindingAggregate(asset) {
  if (directoryAssetIdentity(asset)) return false;
  if (normalizeAssetType(asset?.assetType) !== 5) return false;
  return assetEvidenceValues(asset).some(value =>
    /\baffected scope includes\s+\d+\b/i.test(value)
    || /^\s*\d+\s+(?:accounts?|computers?|groups?|principals?|users?)\b/i.test(value)
  );
}

export function isAdInventoryNoise(asset) {
  if (directoryAssetIdentity(asset)) return false;
  if (normalizeAssetType(asset?.assetType) !== 5) return false;
  if (isAdFindingAggregate(asset)) return true;

  const displayName = String(asset?.displayName || '').trim();
  const hostname = String(asset?.hostname || '').trim();
  const directValues = [displayName, hostname].filter(Boolean);
  const normalizedDisplay = normalizedDirectoryName(displayName);

  if (isCinchAsset(asset) && (CINCH_AD_ACCOUNT_NOISE.has(normalizedDisplay) || normalizedDisplay.startsWith('domaindns:training'))) return true;
  if (AD_CONTAINER_NAMES.has(normalizedDisplay)) return true;
  if (directValues.some(value => /^(?:DC|OU)\s*=/i.test(value))) return true;
  if (directValues.some(value => /^CN\s*=\s*(?:Builtin|Computers|Configuration|Directory Service|ForeignSecurityPrincipals|Optional Features|Services|Sites|System|Users|Windows NT)\b/i.test(value))) return true;
  if (directValues.some(value => /^(?:Principal|PrivilegedObject)\s*:/i.test(value))) return true;
  if (directValues.some(value => /^No (?:affected objects were identified|discrete affected object was returned)/i.test(value))) return true;

  return false;
}

export function adDirectoryPrincipalKeys(asset) {
  const canonical = directoryAssetIdentity(asset);
  if (canonical) return canonical.key ? ['canonical-directory:' + canonical.key] : [];
  if (normalizeAssetType(asset?.assetType) !== 5) return [];
  const identity = adDirectoryIdentity(asset);
  if (identity) {
    return identity.kind === 'user'
      ? [identity.key, `directory:${normalizedDirectoryName(identity.name)}`]
      : [identity.key];
  }

  for (const value of assetEvidenceValues(asset)) {
    const commonName = value.match(/\bCN=([^,()]+)/i);
    if (commonName) {
      const name = normalizedDirectoryName(commonName[1]);
      if (name) return [`directory:${name}`];
    }
  }

  return [];
}

export function adDirectoryDisplayName(asset) {
  const identity = adDirectoryIdentity(asset);
  if (!identity) return '';

  const displayName = String(asset?.displayName || '').trim();
  const displayIsRaw = /^(?:computer|group|serviceaccount|user)\s*:/i.test(displayName)
    || /^(?:CN|DC|OU)\s*=/i.test(displayName);
  if (displayName && !displayIsRaw && !/^demo account$/i.test(displayName)) return displayName;
  return identity.name;
}

function adIdentityRank(asset) {
  const identity = adDirectoryIdentity(asset);
  if (!identity) return 0;
  if (identity.kind === 'computer') return identity.source === 'computer' ? 40 : 30;
  const displayName = String(asset?.displayName || '').trim();
  return /^(?:user|group)\s*:/i.test(displayName) ? 20 : 30;
}

export function clientSafeAssetSearchText(asset) {
  const type = normalizeAssetType(asset?.assetType);
  const displayName = type === 5
    ? adDirectoryDisplayName(asset) || preferredAssetName(asset)
    : preferredAssetName(asset);
  return [displayName, asset?.ipAddress, asset?.assetId]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
}

export function canonicalizeAssets(assets) {
  if (!Array.isArray(assets)) return [];
  const hostNames = new Set(
    assets
      .filter(asset => normalizeAssetType(asset?.assetType) === 2 && !directoryAssetIdentity(asset))
      .map(asset => normalizedMachineName(asset?.hostname || asset?.displayName || ''))
      .filter(Boolean)
  );

  const candidates = assets.filter(asset => {
    if (directoryAssetIdentity(asset)) return true;
    if (isAdInventoryNoise(asset)) return false;
    const computerName = adComputerPrincipalName(asset);
    return !(computerName && hostNames.has(computerName));
  });

  const preferredByIdentity = new Map();
  for (const asset of candidates) {
    for (const key of adDirectoryPrincipalKeys(asset)) {
      const current = preferredByIdentity.get(key);
      if (!current || adIdentityRank(asset) > adIdentityRank(current)) preferredByIdentity.set(key, asset);
    }
  }

  return candidates.filter(asset => {
    const keys = adDirectoryPrincipalKeys(asset);
    return keys.length === 0 || keys.every(key => preferredByIdentity.get(key) === asset);
  });
}

export function assetMetadataLabel(key) {
  const normalized = String(key || '').replace(/[^a-z0-9]/gi, '').toLowerCase();
  if (normalized === 'principal' || normalized === 'principals') return 'Directory identity';
  if (normalized === 'distinguishedname') return 'Directory name / evidence';
  if (normalized === 'objecthash') return 'Source object hash';
  return String(key || '');
}

export function assetDisplayText(value, fallback = '\u2014', stableId = '') {
  if (typeof value !== 'string' || value.trim() === '') return fallback;
  const redacted = redactDemoText(value).trim();
  if (!redacted) return fallback;
  const shortId = String(stableId).replace(/[^a-z0-9]/gi, '').slice(0, 6).toUpperCase();
  if (EMAIL_VALUE.test(redacted)) {
    return `${formatEmailIdentity(redacted)}${shortId ? ` · ${shortId}` : ''}`;
  }
  if (OPAQUE_TAG_VALUE.test(redacted)) return `Managed Asset${shortId ? ` · ${shortId}` : ''}`;
  if (IMPLEMENTATION_VALUE.test(redacted)) return 'Managed Endpoint';
  return redacted;
}

export default function Assets() {
  const navigate = useNavigate();
  const { data: assets, loading, refreshing, isStale, error } = useAssets();
  const [assetLoadTimedOut, setAssetLoadTimedOut] = useState(false);
  const [tab, setTab] = useState('all');
  const [sort, setSort] = useState('name');
  const [sortDir, setSortDir] = useState('asc');
  const [lifecycleFilter, setLifecycleFilter] = useState('all');
  const [q, setQ] = useState('');

  const allAssets = Array.isArray(assets) ? assets : [];
  const inventoryAssets = useMemo(() => canonicalizeAssets(allAssets), [allAssets]);
  const consolidatedCount = allAssets.length - inventoryAssets.length;
  useEffect(() => {
    if (!loading || allAssets.length > 0) {
      setAssetLoadTimedOut(false);
      return undefined;
    }
    const timer = window.setTimeout(() => setAssetLoadTimedOut(true), 2500);
    return () => window.clearTimeout(timer);
  }, [loading, allAssets.length]);
  const lifecycleSummary = useMemo(() => summarizeAssetLifecycle(inventoryAssets), [inventoryAssets]);

  const counts = useMemo(() => ({
    total: inventoryAssets.length,
    hosts: inventoryAssets.filter(a => { const t = normalizeAssetType(a.assetType); return t === 1 || t === 2; }).length,
    domains: inventoryAssets.filter(a => normalizeAssetType(a.assetType) === 3).length,
    cloud: inventoryAssets.filter(a => normalizeAssetType(a.assetType) === 4).length,
    accounts: inventoryAssets.filter(a => normalizeAssetType(a.assetType) === 5).length,
  }), [inventoryAssets]);

  const items = useMemo(() => {
    const activeTab = (Array.isArray(TABS) ? TABS : []).find(t => t.id === tab);
    let filtered = activeTab?.filter ? inventoryAssets.filter(a => activeTab.filter.includes(normalizeAssetType(a.assetType))) : inventoryAssets;

    if (lifecycleFilter !== 'all') {
      filtered = filtered.filter(a => {
        const lifecycle = getAssetLifecycle(a);
        return lifecycleFilter === 'needsAction' ? lifecycle.needsAction : lifecycle.key === lifecycleFilter;
      });
    }

    if (q) {
      const lq = q.toLowerCase();
      filtered = filtered.filter(a => clientSafeAssetSearchText(a).includes(lq));
    }

    const sorted = [...filtered].sort((a, b) => {
      let av, bv;
      if (sort === 'name') { av = adDirectoryDisplayName(a) || preferredAssetName(a); bv = adDirectoryDisplayName(b) || preferredAssetName(b); }
      else if (sort === 'ip') { av = a.ipAddress || ''; bv = b.ipAddress || ''; }
      else if (sort === 'type') { av = a.assetType ?? 0; bv = b.assetType ?? 0; }
      else if (sort === 'first') { av = a.firstSeenAt || ''; bv = b.firstSeenAt || ''; }
      else if (sort === 'last') { av = assetLastSeenAt(a)?.getTime() ?? 0; bv = assetLastSeenAt(b)?.getTime() ?? 0; }
      else if (sort === 'lifecycle') { av = getAssetLifecycle(a).key; bv = getAssetLifecycle(b).key; }
      else { av = ''; bv = ''; }
      if (typeof av === 'string') { const cmp = av.localeCompare(bv); return sortDir === 'asc' ? cmp : -cmp; }
      return sortDir === 'asc' ? av - bv : bv - av;
    });
    return sorted;
  }, [inventoryAssets, tab, lifecycleFilter, q, sort, sortDir]);

  const handleSort = col => {
    if (sort === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSort(col); setSortDir('asc'); }
  };

  const sortArrow = col => sort === col ? (sortDir === 'asc' ? ' ▲' : ' ▼') : '';

  return (
    <Shell title="Asset Inventory" sub={loading && !assetLoadTimedOut ? 'Loading...' : `${counts.total} canonical assets shown`}>
      {error && <PageError error={error} />}
      {(refreshing || isStale || assetLoadTimedOut) && !error && (
        <div style={{ marginBottom: 12, padding: '10px 14px', borderRadius: 6, border: `1px solid ${C.amb}44`, background: `${C.amb}10`, color: C.t2, fontSize: 11, fontFamily: FONTS.mono }}>
          {assetLoadTimedOut && allAssets.length === 0
            ? 'Asset inventory is still loading. Summary counts are available while detailed asset rows finish syncing.'
            : 'Showing cached asset inventory while the latest details refresh.'}
        </div>
      )}
      {consolidatedCount > 0 && (
        <div style={{ marginBottom: 12, padding: '10px 14px', borderRadius: 6, border: `1px solid ${C.sky}44`, background: `${C.sky}10`, color: C.t2, fontSize: 11, fontFamily: FONTS.mono }}>
          {consolidatedCount} exact host/directory duplicate or AD finding-summary {consolidatedCount === 1 ? 'record is' : 'records are'} consolidated from this inventory view. Source scan evidence remains available.
        </div>
      )}
      {/* KPI Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5,1fr)', gap: 10, marginBottom: 12 }}>
        {[
          { l: 'Total Assets', v: counts.total, c: C.sky },
          { l: 'Needs Action', v: lifecycleSummary.needsAction, c: C.amb },
          { l: 'Current', v: lifecycleSummary.active, c: C.grn },
          { l: 'Stale', v: lifecycleSummary.stale, c: C.amb },
          { l: 'Retired / Inactive', v: lifecycleSummary.retired, c: C.t3 },
        ].map(s => (
          <Card key={s.l} style={{ padding: '10px 14px', borderTop: `2px solid ${s.c}` }}>
            <div style={{ fontSize: 9, color: C.t2, textTransform: 'uppercase', letterSpacing: '0.08em', fontFamily: FONTS.mono, marginBottom: 5 }}>{s.l}</div>
            <div style={{ fontSize: 24, fontWeight: 800, color: s.v > 0 ? s.c : C.t3, fontFamily: FONTS.mono }}>{s.v}</div>
          </Card>
        ))}
      </div>

      {/* Tab bar + search */}
      <Card style={{ padding: '8px 10px', marginBottom: 12, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', gap: 3, background: C.s1, border: `1px solid ${C.b1}`, borderRadius: 6, padding: 3 }}>
          {TABS.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)} style={{
              padding: '5px 12px', borderRadius: 5, border: 'none', cursor: 'pointer',
              background: tab === t.id ? C.s2 : 'transparent', color: tab === t.id ? C.t1 : C.t2,
              fontSize: 11, fontWeight: tab === t.id ? 600 : 400, fontFamily: FONTS.sans,
              transition: 'all 0.12s',
            }}>{t.l}</button>
          ))}
        </div>
        <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search assets..." style={{
          flex: 1, minWidth: 160, background: C.s2, border: `1px solid ${C.b1}`, borderRadius: 6,
          padding: '6px 10px', color: C.t1, fontSize: 12, fontFamily: FONTS.mono, outline: 'none',
        }} />
        <select value={lifecycleFilter} onChange={e => setLifecycleFilter(e.target.value)} style={{
          background: C.s2, border: `1px solid ${C.b1}`, borderRadius: 6, color: C.t1,
          padding: '6px 10px', fontSize: 11, fontFamily: FONTS.mono, outline: 'none',
        }}>
          {ASSET_LIFECYCLE_FILTERS.map(f => (
            <option key={f.id} value={f.id}>{f.label}</option>
          ))}
        </select>
        <span style={{ fontSize: 10, color: C.t2, fontFamily: FONTS.mono }}>{items.length} items</span>
      </Card>

      {/* Asset table */}
      <Card>
        <table style={{ borderCollapse: 'collapse', width: '100%' }}>
          <thead>
            <tr style={{ borderBottom: `1px solid ${C.b1}` }}>
              {[
                { l: 'Type', k: 'type' },
                { l: 'Name', k: 'name' },
                { l: 'IP Address', k: 'ip' },
                { l: 'First Seen', k: 'first' },
                { l: 'Last Seen', k: 'last' },
                { l: 'Lifecycle', k: 'lifecycle' },
                { l: 'Tags', k: null },
              ].map(col => (
                <th key={col.l} onClick={col.k ? () => handleSort(col.k) : undefined} style={{
                  padding: '8px 12px', fontSize: 9, color: C.t2, fontFamily: FONTS.mono,
                  textTransform: 'uppercase', letterSpacing: '0.08em', textAlign: 'left',
                  cursor: col.k ? 'pointer' : 'default', userSelect: 'none',
                  background: C.s2, whiteSpace: 'nowrap',
                }}>{col.l}{col.k ? sortArrow(col.k) : ''}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading && !assetLoadTimedOut && Array.from({ length: 5 }).map((_, i) => (
              <tr key={i} style={{ borderBottom: `1px solid ${C.b2}` }}>
                {Array.from({ length: 7 }).map((__, j) => (
                  <td key={j} style={{ padding: '10px 12px' }}><Skeleton height={12} /></td>
                ))}
              </tr>
            ))}
            {(!loading || assetLoadTimedOut) && items.length === 0 && (
              <tr><td colSpan={7}>
                <EmptyState
                  title={assetLoadTimedOut && counts.total > 0 ? 'Asset rows are still loading' : allAssets.length === 0 ? 'No assets discovered yet' : 'No assets match your filters'}
                  sub={assetLoadTimedOut && counts.total > 0 ? `${counts.total} assets are counted in the summary. Detailed inventory rows are still syncing from the backend.` : allAssets.length === 0 ? 'Assets will populate after your first scan completes.' : 'Try adjusting search or tab filters'}
                />
              </td></tr>
            )}
            {items.map(a => {
              const tags = assetDisplayTags(a.tags);
              const typeNum = normalizeAssetType(a.assetType);
              const presentationTypeNum = assetPresentationType(a);
              const directoryIdentity = adDirectoryIdentity(a);
              const directoryComputer = directoryIdentity?.kind === 'computer';
              const directoryTypeLabels = { computer: 'AD Computer', group: 'AD Group', user: 'AD User' };
              const typeLabel = directoryIdentity ? directoryTypeLabels[directoryIdentity.kind] || 'Directory identity' : ASSET_TYPE_LABELS[presentationTypeNum] || `Type ${a.assetType}`;
              const typeColor = directoryComputer ? C.blu : ASSET_TYPE_COLORS[presentationTypeNum] || C.t2;
              const TypeIcon = directoryComputer ? Monitor : ASSET_TYPE_ICONS[presentationTypeNum] || User;
              const rawName = adDirectoryDisplayName(a) || preferredAssetName(a);
              const name = assetDisplayText(rawName, '\u2014', a.assetId);
              const hostname = typeNum === 5 ? '\u2014' : assetDisplayText(a.hostname || '', '\u2014', a.assetId);
              const lifecycle = getAssetLifecycle(a);
              const lifecycleColor = LIFECYCLE_COLORS[lifecycle.key] || C.t2;
              const lastSeenValue = assetLastSeenAt(a);
              return (
                <TR key={a.assetId} onClick={() => navigate(`/client/assets/${a.assetId}`)} style={{ cursor: 'pointer' }}>
                  <TD>
                    <span style={{
                      display: 'inline-flex', alignItems: 'center', gap: 4,
                      fontSize: 9, fontWeight: 700, color: typeColor, fontFamily: FONTS.mono,
                      background: `${typeColor}15`, padding: '2px 6px', borderRadius: 3,
                      border: `1px solid ${typeColor}33`, whiteSpace: 'nowrap',
                    }}><TypeIcon size={11} strokeWidth={2} />{typeLabel}</span>
                  </TD>
                  <TD style={{ maxWidth: 260 }}>
                    <div style={{ fontSize: 12, color: C.t1, fontWeight: 500 }}>{name}</div>
                    {hostname !== '\u2014' && hostname !== name && (
                      <div style={{ fontSize: 9, color: C.t2, fontFamily: FONTS.mono, marginTop: 1 }}>{hostname}</div>
                    )}
                  </TD>
                  <TD><span style={{ fontSize: 11, fontFamily: FONTS.mono, color: C.t1 }}>{a.ipAddress || '\u2014'}</span></TD>
                  <TD>
                    <span style={{ fontSize: 10, color: C.t2, fontFamily: FONTS.mono, whiteSpace: 'nowrap' }} title={a.firstSeenAt ? exactTime(a.firstSeenAt) : ''}>
                      {a.firstSeenAt ? relativeTime(a.firstSeenAt) : '\u2014'}
                    </span>
                  </TD>
                  <TD>
                    <span style={{ fontSize: 10, color: C.t2, fontFamily: FONTS.mono, whiteSpace: 'nowrap' }} title={lastSeenValue ? exactTime(lastSeenValue) : ''}>
                      {lastSeenValue ? relativeTime(lastSeenValue) : '\u2014'}
                    </span>
                  </TD>
                  <TD>
                    <span title={lifecycle.reason} style={{
                      display: 'inline-flex', alignItems: 'center', gap: 5,
                      fontSize: 9, fontWeight: 700, fontFamily: FONTS.mono,
                      color: lifecycleColor,
                      background: `${lifecycleColor}15`,
                      padding: '2px 7px', borderRadius: 3,
                      border: `1px solid ${lifecycleColor}33`,
                      whiteSpace: 'nowrap',
                    }}>
                      <span style={{ width: 5, height: 5, borderRadius: '50%', background: lifecycleColor }} />
                      {lifecycle.label}
                    </span>
                  </TD>
                  <TD>
                    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                      {tags.slice(0, 3).map((t, i) => <Tag key={i} label={t} color={C.sky} />)}
                      {tags.length > 3 && <span style={{ fontSize: 9, color: C.t3, fontFamily: FONTS.mono }}>+{tags.length - 3}</span>}
                    </div>
                  </TD>
                </TR>
              );
            })}
          </tbody>
        </table>
        <div style={{ padding: '8px 14px', borderTop: `1px solid ${C.b1}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: 10, color: C.t2, fontFamily: FONTS.mono }}>{items.length} of {inventoryAssets.length} canonical assets</span>
        </div>
      </Card>
    </Shell>
  );
}
